import { createAsyncThunk } from "@reduxjs/toolkit";
import { ENV, FIREBASE_APP_ID } from "gatsby-env-variables";
import {
  DEFAULT_REMOTE_CONFIG_PARAMETERS,
  TRemoteConfig,
} from "./remoteConfiguration.reducer";
import getCookie from "@utils/getCookie";
import { logException } from "../../helpers/logException/logException";
import initFirebase from "../../utils/initFirebase";

/**
 * Says which switch the app is reading. A flag can arrive from the console, from
 * the SDK's hour-long cache, or from the fallback when Firebase is unreachable,
 * and on screen the three are indistinguishable. The app id is here because it
 * decides which of the parameter's conditions applies.
 */
const logSource = (source: string, config: TRemoteConfig): void => {
  if (ENV === "prod") return;
  // eslint-disable-next-line no-console
  console.log(`[remote-config] ${source}`, {
    appId: FIREBASE_APP_ID,
    ...config,
    // A cookie beats remote config in the selectors, so it belongs in the same
    // picture as the values it can override.
    cookies: {
      FF_APP_V3: getCookie("FF_WEB_V3_INCOME_FUND"),
      FF_HOMEPAGE: getCookie("FF_HOMEPAGE"),
    },
  });
};

export const fetchRemoteConfigThunk = createAsyncThunk<TRemoteConfig, void>(
  "REMOTE_CONFIG:FETCH_CONFIG",
  async () => {
    try {
      const remoteConfig = await initFirebase();
      if (!remoteConfig) {
        logSource(
          "offline or unavailable, using defaults",
          DEFAULT_REMOTE_CONFIG_PARAMETERS
        );
        return DEFAULT_REMOTE_CONFIG_PARAMETERS;
      }

      logSource("from firebase", remoteConfig);
      return remoteConfig;
    } catch (e: unknown) {
      logException({
        message: "Error fetching remote config",
        exception: e,
      });
      logSource(
        "fetch failed, using defaults",
        DEFAULT_REMOTE_CONFIG_PARAMETERS
      );
      return DEFAULT_REMOTE_CONFIG_PARAMETERS;
    }
  }
);
